import { useCallback, useEffect, useRef } from "react"

/**
 * Returns a debounced wrapper around `callback`, plus `cancel` and `flush` for timer control.
 * `flush` runs any pending invocation immediately with the last arguments.
 */
export function useDebouncedCallback<TArgs extends readonly unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs: number,
): {
  debounced: (...args: TArgs) => void
  cancel: () => void
  flush: () => void
} {
  const callbackRef = useRef(callback)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingRef = useRef<TArgs | null>(null)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const cancel = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    pendingRef.current = null
  }, [])

  const flush = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    const args = pendingRef.current
    pendingRef.current = null
    if (args) {
      callbackRef.current(...args)
    }
  }, [])

  const debounced = useCallback(
    (...args: TArgs) => {
      pendingRef.current = args
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
      if (delayMs <= 0) {
        queueMicrotask(() => {
          const pending = pendingRef.current
          pendingRef.current = null
          if (pending) {
            callbackRef.current(...pending)
          }
        })
        return
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null
        const pending = pendingRef.current
        pendingRef.current = null
        if (pending) {
          callbackRef.current(...pending)
        }
      }, delayMs)
    },
    [delayMs],
  )

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
    },
    [],
  )

  return { debounced, cancel, flush }
}
